import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { generateSlots } from "@/utils/slots";
import { Search, Zap, Clock, User, Car, MapPin, Wrench, CalendarDays, X } from "lucide-react";
import axios from "axios";
import { format, addDays } from "date-fns";
import { es } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_MAP = ["domingo", "lunes", "martes", "miercoles", "jueves", "viernes", "sabado"];

export default function QuickReception({ onClose, onCreated, config }) {
  const { axiosAuth } = useAuth();
  const searchRef = useRef(null);
  const [searchQ, setSearchQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [centers, setCenters] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [saving, setSaving] = useState(false);
  const [clientFound, setClientFound] = useState(false);
  const [lastVisitInfo, setLastVisitInfo] = useState(null);
  const [clientVip, setClientVip] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);

  const [f, setF] = useState({
    client_name: "", client_phone: "", client_email: "",
    plate: "", brand: "", model_name: "",
    center: "", center_name: "",
    advisor_id: "", advisor_name: "",
    date: format(new Date(), "yyyy-MM-dd"),
    time_slot: "", service_description: "",
  });

  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  // Dynamic services from config (only active, sorted by order)
  const configServices = (config?.services || [])
    .filter(s => typeof s === "object" ? s.active !== false : true)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map(s => typeof s === "string" ? s : s.name);

  // Get working hours for the selected center (or fall back to global config)
  const selectedCenter = centers.find(c => (c.slug || c.id) === f.center);
  const workingHours = selectedCenter?.working_hours || config?.working_hours || {};

  // Check if a date is a working day
  const isWorkingDay = useCallback((date) => {
    const dayKey = DAY_MAP[date.getDay()];
    const dayRange = workingHours[dayKey];
    if (!dayRange || dayRange.toLowerCase() === "cerrado") return false;
    const dateStr = format(date, "yyyy-MM-dd");
    if (holidays.includes(dateStr)) return false;
    return true;
  }, [workingHours, holidays]);

  // Generate next N working days for quick buttons
  const getNextWorkingDays = useCallback((count = 5) => {
    const result = [];
    let offset = 0;
    while (result.length < count && offset < 30) {
      const d = addDays(new Date(), offset);
      if (isWorkingDay(d)) {
        result.push({ date: d, dateStr: format(d, "yyyy-MM-dd"), offset });
      }
      offset++;
    }
    return result;
  }, [isWorkingDay]);

  // Load centers and holidays on mount
  useEffect(() => {
    axios.get(`${API}/centers`).then(r => setCenters(r.data)).catch(() => {});
    axios.get(`${API}/holidays/dates`).then(r => setHolidays(r.data)).catch(() => {});
    setTimeout(() => searchRef.current?.focus(), 200);
  }, []);

  // Set initial date to first working day
  useEffect(() => {
    const today = new Date();
    if (!isWorkingDay(today)) {
      const workingDays = getNextWorkingDays(1);
      if (workingDays.length > 0) {
        set("date", workingDays[0].dateStr);
      }
    }
  }, [holidays, workingHours]);

  // Load advisors when center changes
  useEffect(() => {
    if (!f.center) { setAdvisors([]); return; }
    axios.get(`${API}/advisors/by-center/${f.center}`).then(r => setAdvisors(r.data)).catch(() => setAdvisors([]));
  }, [f.center]);

  // Load availability when date/center/advisor changes
  useEffect(() => {
    if (!f.date || !f.center) { setAvailability([]); return; }
    const advParam = f.advisor_id ? `&advisor_id=${f.advisor_id}` : "";
    axios.get(`${API}/appointments/availability?date=${f.date}&center=${f.center}${advParam}`)
      .then(r => setAvailability(r.data.slots || []))
      .catch(() => setAvailability([]));
  }, [f.date, f.center, f.advisor_id]);

  // Search clients debounced
  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setSuggestions([]); return; }
    setSearching(true);
    try {
      const res = await axiosAuth().get(`/appointments/search-clients?q=${encodeURIComponent(q)}`);
      setSuggestions(res.data);
    } catch { setSuggestions([]); }
    setSearching(false);
  }, [axiosAuth]);

  useEffect(() => {
    const t = setTimeout(() => doSearch(searchQ), 300);
    return () => clearTimeout(t);
  }, [searchQ, doSearch]);

  const selectClient = (client) => {
    setF(prev => ({
      ...prev,
      client_name: client.client_name || prev.client_name,
      client_phone: client.client_phone || prev.client_phone,
      client_email: client.client_email || prev.client_email,
      plate: client.plate || prev.plate,
      brand: client.brand || prev.brand,
      model_name: client.model_name || prev.model_name,
      center: client.center || prev.center,
      center_name: client.center_name || prev.center_name,
      advisor_id: client.advisor_id || prev.advisor_id,
      advisor_name: client.advisor_name || prev.advisor_name,
    }));
    setClientFound(true);
    setLastVisitInfo({ date: client.last_date, service: client.last_service, advisor: client.advisor_name, center: client.center_name });
    setSuggestions([]);
    setSearchQ("");
    // Fetch VIP status
    const params = new URLSearchParams();
    if (client.client_phone) params.set("phone", client.client_phone);
    if (client.plate) params.set("plate", client.plate);
    if (client.client_email) params.set("email", client.client_email);
    axiosAuth().get(`/appointments/client-history?${params}`)
      .then(r => setClientVip(r.data))
      .catch(() => setClientVip(null));
  };

  // Available slots for selected date's working hours
  const interval = config?.appointment_interval || 20;
  const dayKey = f.date ? DAY_MAP[new Date(f.date + "T12:00:00").getDay()] : "";
  const dayRange = workingHours[dayKey];
  const allSlots = generateSlots(dayRange, interval);

  const availableSlots = allSlots.map(s => {
    const info = availability.find(a => a.slot === s);
    return { slot: s, available: info ? info.available : true, free: info?.free_advisors ?? 0 };
  });

  const suggestedSlots = f.time_slot && !availableSlots.find(s => s.slot === f.time_slot && s.available)
    ? availableSlots.filter(s => s.available && s.slot > f.time_slot).slice(0, 3)
    : [];

  // Service multi-select
  const toggleService = (svc) => {
    setSelectedServices(prev =>
      prev.includes(svc) ? prev.filter(s => s !== svc) : [...prev, svc]
    );
  };

  const removeService = (svc) => {
    setSelectedServices(prev => prev.filter(s => s !== svc));
  };

  const serviceType = selectedServices.join(" + ");
  const canSubmit = f.client_name && f.client_phone && f.plate && f.center && f.date && f.time_slot && selectedServices.length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) { toast.error("Completa los campos obligatorios"); return; }
    setSaving(true);
    try {
      await axiosAuth().post("/appointments", {
        client_name: f.client_name,
        client_phone: f.client_phone,
        client_email: f.client_email,
        plate: f.plate,
        brand: f.brand,
        model_name: f.model_name,
        center: f.center,
        center_name: f.center_name,
        advisor_id: f.advisor_id,
        advisor_name: f.advisor_name,
        date: f.date,
        time_slot: f.time_slot,
        service_type: serviceType,
        notes: f.service_description,
      });
      toast.success("Cita creada correctamente");
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear la cita");
    }
    setSaving(false);
  };

  const quickDays = getNextWorkingDays(5);

  return (
    <div className="space-y-4" data-testid="quick-reception">
      {/* Search bar */}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            ref={searchRef}
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
            placeholder="Buscar por matricula, telefono, nombre o email..."
            className="pl-10 h-10 rounded-sm border-slate-300 text-sm"
            data-testid="quick-search-input"
          />
          {searching && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">Buscando...</span>}
        </div>
        {suggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-60 overflow-auto" data-testid="quick-suggestions">
            {suggestions.map((s, i) => (
              <button key={i} onClick={() => selectClient(s)}
                className="w-full text-left p-2.5 hover:bg-slate-50 border-b border-slate-100 last:border-0 transition-colors"
                data-testid={`suggestion-${i}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-semibold text-slate-900">{s.client_name}</span>
                    <span className="text-xs text-slate-400 ml-2 font-mono">{s.plate}</span>
                  </div>
                  <span className="text-[10px] text-slate-400">{s.last_date}</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {s.client_phone && <span className="mr-3">{s.client_phone}</span>}
                  {s.brand && <span className="mr-3">{s.brand} {s.model_name}</span>}
                  {s.last_service && <span className="text-slate-400">Ultimo: {s.last_service}</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Last visit info + VIP */}
      {clientFound && clientVip?.is_vip && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-2.5 flex items-center gap-3" data-testid="vip-reception-badge">
          <Zap className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <div className="text-[11px] text-amber-800">
            <strong>CLIENTE VIP</strong> &mdash; {clientVip.total_visits} visitas
            {lastVisitInfo?.date && <> &middot; Ultima: {lastVisitInfo.date}</>}
            {clientVip.usual_center && <> &middot; Centro: {clientVip.usual_center}</>}
            {clientVip.usual_advisor && <> &middot; Asesor: {clientVip.usual_advisor}</>}
          </div>
        </div>
      )}
      {clientFound && lastVisitInfo && !clientVip?.is_vip && (
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-2.5 flex items-center gap-3" data-testid="last-visit-info">
          <Clock className="w-4 h-4 text-blue-500 flex-shrink-0" />
          <div className="text-[11px] text-blue-700">
            <strong>Cliente conocido</strong>
            {clientVip && <> &mdash; {clientVip.total_visits} visitas</>}
            {lastVisitInfo.date && <> &middot; Ultima: {(() => { const p = lastVisitInfo.date?.split("-"); return p?.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : lastVisitInfo.date; })()}</>}
            {lastVisitInfo.service && <> &middot; {lastVisitInfo.service}</>}
          </div>
        </div>
      )}

      {/* Form - compact 3-column grid */}
      <div className="grid grid-cols-3 gap-3">
        {/* Column 1: Client + Vehicle */}
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1"><User className="w-3 h-3" /> Cliente</p>
          <Input value={f.client_name} onChange={e => set("client_name", e.target.value)} placeholder="Nombre *" className="h-8 rounded-sm text-sm" data-testid="quick-name" />
          <Input value={f.client_phone} onChange={e => set("client_phone", e.target.value)} placeholder="Telefono *" className="h-8 rounded-sm text-sm" data-testid="quick-phone" />
          <Input value={f.client_email} onChange={e => set("client_email", e.target.value)} placeholder="Email" className="h-8 rounded-sm text-sm" data-testid="quick-email" />
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1 pt-1"><Car className="w-3 h-3" /> Vehiculo</p>
          <Input value={f.plate} onChange={e => set("plate", e.target.value.toUpperCase())} placeholder="Matricula *" className="h-8 rounded-sm text-sm font-mono" data-testid="quick-plate" />
          <div className="flex gap-1">
            <Input value={f.brand} onChange={e => set("brand", e.target.value)} placeholder="Marca" className="h-8 rounded-sm text-sm flex-1" data-testid="quick-brand" />
            <Input value={f.model_name} onChange={e => set("model_name", e.target.value)} placeholder="Modelo" className="h-8 rounded-sm text-sm flex-1" data-testid="quick-model" />
          </div>
        </div>

        {/* Column 2: Center + Advisor + Date */}
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> Centro y Asesor</p>
          <Select value={f.center} onValueChange={v => { const c = centers.find(x => (x.slug || x.id) === v); set("center", v); set("center_name", c?.name || v); set("advisor_id", ""); set("advisor_name", ""); }}>
            <SelectTrigger className="h-8 rounded-sm text-sm" data-testid="quick-center"><SelectValue placeholder="Centro *" /></SelectTrigger>
            <SelectContent>{centers.map(c => <SelectItem key={c.slug || c.id} value={c.slug || c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={f.advisor_id || "_none"} onValueChange={v => { if (v === "_none") { set("advisor_id", ""); set("advisor_name", ""); } else { const a = advisors.find(x => x.id === v); set("advisor_id", v); set("advisor_name", a?.name || ""); } set("time_slot", ""); }}>
            <SelectTrigger className="h-8 rounded-sm text-sm" data-testid="quick-advisor"><SelectValue placeholder="Asesor" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">Primer disponible</SelectItem>
              {advisors.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1 pt-1"><CalendarDays className="w-3 h-3" /> Fecha</p>
          <Input type="date" value={f.date} onChange={e => { set("date", e.target.value); set("time_slot", ""); }} className="h-8 rounded-sm text-sm" data-testid="quick-date" />
          {/* Quick date buttons - only working days */}
          <div className="flex gap-1" data-testid="quick-date-buttons">
            {quickDays.map((day, i) => {
              const label = day.offset === 0 ? "Hoy" : day.offset === 1 ? "Manana" : format(day.date, "EEE d", { locale: es });
              return (
                <button key={day.dateStr} type="button" onClick={() => { set("date", day.dateStr); set("time_slot", ""); }}
                  className={`flex-1 text-[9px] py-1 rounded-sm font-semibold uppercase transition-colors ${f.date === day.dateStr ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                  data-testid={`quick-date-${i}`}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Column 3: Time + Service */}
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Hora</p>
          {f.center && f.date ? (
            <div className="grid grid-cols-4 gap-px max-h-[140px] overflow-y-auto border border-slate-200 rounded-sm p-0.5" data-testid="quick-slots-grid">
              {availableSlots.map(s => (
                <button key={s.slot} type="button" disabled={!s.available}
                  onClick={() => set("time_slot", s.slot)}
                  className={`text-[10px] font-mono py-1 rounded-sm transition-colors ${
                    f.time_slot === s.slot ? 'bg-red-600 text-white font-bold' :
                    !s.available ? 'bg-slate-100 text-slate-300 cursor-not-allowed line-through' :
                    'bg-white text-slate-700 hover:bg-blue-50'
                  }`}
                  data-testid={`quick-slot-${s.slot}`}>
                  {s.slot}
                </button>
              ))}
              {availableSlots.length === 0 && <p className="col-span-4 text-[10px] text-center text-slate-400 py-2">Sin horario disponible</p>}
            </div>
          ) : (
            <p className="text-[10px] text-slate-400 py-2">Selecciona centro y fecha</p>
          )}
          {suggestedSlots.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-1.5" data-testid="quick-suggestions-alt">
              <p className="text-[9px] text-amber-700 font-semibold mb-1">Huecos alternativos:</p>
              <div className="flex gap-1">
                {suggestedSlots.map(s => (
                  <button key={s.slot} onClick={() => set("time_slot", s.slot)}
                    className="text-[10px] font-mono px-2 py-0.5 bg-white border border-amber-300 rounded-sm hover:bg-amber-100 text-amber-800 font-semibold">
                    {s.slot}
                  </button>
                ))}
              </div>
            </div>
          )}

          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1 pt-1"><Wrench className="w-3 h-3" /> Servicio</p>

          {/* Selected services chips */}
          {selectedServices.length > 0 && (
            <div className="flex flex-wrap gap-1" data-testid="quick-selected-services">
              {selectedServices.map(svc => (
                <span key={svc} className="inline-flex items-center gap-0.5 bg-red-50 text-red-700 text-[10px] px-2 py-0.5 rounded-sm font-medium border border-red-200">
                  {svc}
                  <button type="button" onClick={() => removeService(svc)}
                    className="text-red-400 hover:text-red-600 ml-0.5"
                    data-testid={`remove-svc-chip`}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Service buttons from config */}
          <div className="flex flex-wrap gap-1" data-testid="quick-services">
            {configServices.map(svc => (
              <button key={svc} type="button" onClick={() => toggleService(svc)}
                className={`text-[9px] px-2 py-1 rounded-sm font-semibold uppercase transition-colors ${
                  selectedServices.includes(svc) ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`quick-svc-btn`}>
                {svc}
              </button>
            ))}
          </div>

          {configServices.length === 0 && (
            <p className="text-[10px] text-amber-600" data-testid="no-services-warning">Sin servicios configurados. Anadir en Configuracion.</p>
          )}

          <Textarea value={f.service_description} onChange={e => set("service_description", e.target.value)} placeholder="Comentario / descripcion..." className="h-12 rounded-sm text-sm resize-none" data-testid="quick-comment" />
        </div>
      </div>

      <DialogFooter className="gap-2 pt-2 border-t border-slate-200">
        <Button variant="outline" onClick={onClose} className="rounded-sm text-xs uppercase" data-testid="quick-cancel-btn">Cancelar</Button>
        <Button onClick={handleSubmit} disabled={!canSubmit || saving}
          className="bg-red-600 hover:bg-red-700 rounded-sm text-xs uppercase font-bold px-6" data-testid="quick-submit-btn">
          <Zap className="w-3.5 h-3.5 mr-1" /> {saving ? "Creando..." : "Crear Cita"}
        </Button>
      </DialogFooter>
    </div>
  );
}
